import { Fragment, useCallback, useEffect, useState } from "react";
import { useGlobal } from "reactn";
import cx from "classnames";
import useLanguage from "../useLanguage";
import PropertyHeader from "./PropertyHeader";
import SaveButton from "./SaveButton";
import ReportButton from "./ReportButton";
import ProperyImages from "./PropertyImages";
import ResidentialFeatures from "./ResidentialFeatures";
import CondoFeatures from "./CondoFeatures";
import PropertyValuation from "./PropertyValuation";
import PropertyOverlordButton from "./PropertyOverlordButton";
import withPropertyFeatures from "../withPropertyFeatures";
import { isCondo, isResidential } from "../../lib/features-config";
import { getGlobalValue, setGlobalValue } from "../../lib/global-util";
import { removeStoredData, storeInitAddress } from "../../lib/stored-data";
import { isEmpty, lowerCase } from "../../lib/util";
import { goHome } from "../../lib/session";
import getLogger from "../../lib/debug-logger";

import "../../styles/avm/property-view.scss";
import "../../styles/avm/property-features.scss";
import "../../styles/avm/property-explained.scss";

const log = getLogger("PropertyView", 1);

//-------------------------------------- TEMP -- AVM description: sort of fix for incorrect HTML list
const unorder = (html) =>
  (html && html.replace(/<ol/gi, "<ul").replace(/<\/ol/gi, "</ul")) || "";

const PropertyView = (props) => {
  const { featuresPresent, saveEnabled } = props; // from withPropertyFeatures HOC

  const [addressLine] = useGlobal("ADDRESS_LINE");
  const [propertyType] = useGlobal("PROPERTY_TYPE");
  const [overlord] = useGlobal("OVERLORD");

  // construction feats sources (exist for logged in as admin only)
  const [featSources] = useGlobal("FEATURES_SOURCES");

  const [resetEnabled] = useGlobal("RESET_ENABLED");

  // prefill data error, if present
  const [dataError] = useGlobal("DATA_ERROR");

  // Explainability content
  const [avmDescription] = useGlobal("AVM_DESCRIPTION");

  const [explained, setExplained] = useState(false);

  const { t, language } = useLanguage();

  const toggleExplained = useCallback(() => {
    if (explained) {
      setExplained(false);
    } else if (avmDescription) {
      setExplained(true);
    }
  }, [avmDescription, explained]);

  useEffect(() => {
    !!dataError && log(t("PREFILL_ERROR")); //------------------ log
  }, [dataError, t]);

  // trigger confirmation dialog preventing from leaving the page if edited features not saved;
  // don't use reactive values in this event handler
  useEffect(() => {
    const watcher = window.addEventListener("beforeunload", (ev) => {
      if (isEmpty(getGlobalValue("DIRTY_KEYS"))) return;
      ev.returnValue = "";
      ev.preventDefault();
      return "";
    });
    return () => {
      window.removeEventListener("beforeunload", watcher);
    };
  });

  // Close explainability box on any click
  useEffect(() => {
    const watcher = window.addEventListener("click", () => {
      setExplained(false);
    });
    return () => window.removeEventListener("click", watcher);
  }, [setExplained]);

  // Reset logic: store current address and reload page
  const handleReset = () => {
    log("reset triggered"); //-------------- log
    storeInitAddress(addressLine);
    removeStoredData();
    setGlobalValue("DIRTY_KEYS", []); // this disables unload confirmation dialog
    goHome(); //--------------------- triggers page reload
  };

  return (
    <Fragment>
      <PropertyHeader {...props}>
        <div className="header-controls">
          <SaveButton disabled={!saveEnabled} />

          <button
            className={cx("big-button", "reset-btn", language)}
            onClick={handleReset}
            disabled={!resetEnabled}
          >
            {t("Reset")}
          </button>
          <ReportButton />
        </div>
      </PropertyHeader>
      
      {!!dataError && <p className={error}>{t("DATA_ERROR")}</p>}

      <div className="property-view">
        <div className="property-view-images">
          <ProperyImages />
          {!!overlord && (
            <div className="pv-overlord">
              <PropertyOverlordButton />
            </div>
          )}
        </div>

        <div
          className={cx("property-view-main", lowerCase(propertyType), {
            condensed: !!featSources,
          })}
        >
          

          {featuresPresent && (
            <Fragment>
              {isResidential(propertyType) && <ResidentialFeatures {...props} />}
              {isCondo(propertyType) && <CondoFeatures {...props} />}

              <PropertyValuation
                isExplained={!!avmDescription}
                handleExplainability={toggleExplained}
              />

              {props.children}
            </Fragment>
          )}

          {explained && (
            <div
              className="property-explained"
              dangerouslySetInnerHTML={{ __html: unorder(avmDescription) }}
              onClick={() => {
                setExplained(false);
              }}
            ></div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default withPropertyFeatures(PropertyView);
